"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  label: string;
  value: number;
  prefix: string;
  unit: string;
  description: string;
  accent: boolean;
  displayFormatter: string;
};
const metrics: Metric[] = [{
  label: "// AVG PH STABILITY",
  value: 98,
  prefix: "",
  unit: "%",
  description: "Average chemical balance consistency maintained across our managed aquatic systems.",
  accent: false,
  displayFormatter: "plain"
}, {
  label: "// REPAIR EFFICIENCY",
  value: 92,
  prefix: "",
  unit: "%",
  description: "Mean first-visit resolution rate for all mechanical and filtration system repairs.",
  accent: true,
  displayFormatter: "plain"
}, {
  label: "// PROJECTS COMPLETED",
  value: 4617,
  prefix: "",
  unit: "",
  description: "Custom pool builds, repairs, renovations, and system upgrades delivered since 2018.",
  accent: false,
  displayFormatter: "plain"
}, {
  label: "// ACTIVE CLIENTS",
  value: 282,
  prefix: "",
  unit: "",
  description: "Residential and commercial properties under our professional maintenance care.",
  accent: false,
  displayFormatter: "comma"
}];
function CountUp({
  end,
  start,
  formatter
}: {
  end: number;
  start: boolean;
  formatter: string;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1200;
    const startTime = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setCount(end);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [end, start]);
  const formatted = formatter === "comma" ? count.toLocaleString("en-US") : count.toString();
  return <>{formatted}</>;
}
export default function Metrics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="4307" id="operational-excellence" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div ref={ref} className="max-w-[1400px] mx-auto">
        {}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-16 md:mb-24">
          <span className="font-mono text-xs md:text-sm tracking-widest text-primary">
            // OPERATIONAL METRICS
          </span>
          <span className="font-mono text-xs md:text-sm tracking-widest text-foreground/60">MEASURED. VERIFIED. DOCUMENTED.</span>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map((metric, i) => <div data-index={i} key={i} className={`relative px-4 md:px-6 py-8 lg:py-4 min-w-0 overflow-hidden ${i > 0 ? "lg:border-l border-border lg:border-t-0 border-t sm:border-t-0" : ""} ${i === 1 ? "sm:border-l border-border" : ""} ${i === 2 ? "sm:border-l-0 lg:border-l border-border sm:border-t border-t sm:lg:border-t-0" : ""} ${i === 3 ? "sm:border-l border-border sm:border-t lg:border-t-0" : ""}`}>
              <div className="font-mono text-[10px] md:text-xs tracking-widest text-foreground/70 mb-6">
                {metric.label}
              </div>

              <div className="flex items-start mb-6 w-full">
                <span className={`font-sans font-extrabold leading-[0.85] ${i === 2 ? "tracking-normal w-full block" : "tracking-tighter"} text-[64px] sm:text-[80px] md:text-[100px] lg:text-[110px] xl:text-[120px] ${metric.accent ? "text-primary" : "text-foreground"}`} style={{
              fontWeight: 800
            }}>
                  {metric.prefix}
                  <CountUp end={metric.value} start={isVisible} formatter={metric.displayFormatter} />
                </span>
                {metric.unit && <span className={`font-serif italic text-2xl md:text-3xl lg:text-4xl mt-3 md:mt-5 ml-1 ${metric.accent ? "text-primary" : "text-foreground"}`}>
                    {metric.unit}
                  </span>}
              </div>

              <p className="font-serif text-base md:text-lg leading-snug text-foreground/80 max-w-[220px]">
                {metric.description}
              </p>
            </div>)}
        </div>

        {}
        <div className="mt-20 md:mt-28 pt-6 border-t border-border flex justify-end">
          <span className="font-mono text-[10px] md:text-xs tracking-widest text-foreground/70">DATA AS OF Q3.2026</span>
        </div>

        {}
        <div className="flex justify-end mt-12 md:mt-16">
          <div className="relative px-6 py-4">
            {}
            <span className="absolute top-0 left-0 w-3 h-3 border-l border-t border-border" />
            {}
            <span className="absolute top-0 right-0 w-3 h-3 border-r border-t border-border" />
            {}
            <span className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-border" />
            {}
            <span className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-border" />
            <span className="font-serif italic text-sm md:text-base text-foreground">Their Best is Our Baseline.</span>
          </div>
        </div>
      </div>
    </section>;
}